import React, { useRef, useEffect, useState, useCallback } from 'react';
import SkeletonRenderer from '@lib/skeleton-renderer';
import { IPoseData } from '@interfaces/pose.interface';
import styled from '@emotion/styled';

interface SkeletonCanvasProps {
  type: 'screen' | 'frame';
  currentFrameData: IPoseData | null;
  setCurrentFrameData?: (data: any) => void;
  setIsUpdated?: (data: boolean) => void;
}

const ratio169 = 0.5625;

const SkeletonCanvas: React.FC<SkeletonCanvasProps> = ({
  type,
  currentFrameData,
  setCurrentFrameData,
  setIsUpdated,
}) => {
  const [canvasDimensionRatio, setCanvasDimensionRatio] = useState(0); // height/width
  const [canvasHeight, setCanvasHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<SkeletonRenderer>();

  const trackCallback = useCallback(
    (tracked) => {
      switch (tracked.action) {
        case 'moving':
          if (setCurrentFrameData) {
            setCurrentFrameData((prevState: any) => {
              const newX = tracked.movedX / tracked.offset.x;
              const newY = tracked.movedY / tracked.offset.y;

              const position = {
                x: newX < 0 ? 0 : newX > 640 ? 640 : newX,
                y: newY < 0 ? 0 : newY > 480 ? 480 : newY,
              };

              const currentJoint =
                prevState.data.keypoints[
                  prevState.data.keypoints.findIndex(
                    (keypoint: any) => keypoint.part === tracked.name!.replace(/joint-/, ''),
                  )
                ];

              if (!currentJoint['byUser']) {
                currentJoint['originPosition'] = currentJoint.position;
                currentJoint['byUser'] = true;
              }
              currentJoint['isNew'] = true;
              currentJoint['updatedAt'] = +new Date();
              currentJoint.position = position;
              rendererRef.current!.render(prevState);
              setIsUpdated!(true);

              return prevState;
            });
            return currentFrameData;
          }
          break;
        default:
      }
    },
    [currentFrameData, setCurrentFrameData, setIsUpdated],
  );

  useEffect(() => {
    setCanvasDimensionRatio(containerRef.current!.clientHeight / containerRef.current!.clientWidth);
    setCanvasHeight(containerRef.current!.clientHeight);

    if (!rendererRef.current && canvasRef.current) {
      const isModifiable = type === 'screen';
      const isByUser = currentFrameData?.data.keypoints.some((d) => d.byUser);

      rendererRef.current = new SkeletonRenderer(canvasRef.current, {
        isModifiable,
        background: type === 'frame' ? (isByUser ? 'rgba(129,0,255,0.3)' : '#fff') : 'transparent',
      });
      if (isModifiable) rendererRef.current.track((tracked: any) => trackCallback(tracked));
    }

    if (rendererRef.current && type === 'screen')
      rendererRef.current!.setDimensions({
        width:
          canvasDimensionRatio > ratio169
            ? canvasRef.current!.clientWidth
            : canvasRef.current!.clientHeight * 1.7777777778,
        height:
          canvasDimensionRatio > ratio169
            ? canvasRef.current!.clientWidth * ratio169
            : canvasRef.current!.clientHeight,
      });

    window.addEventListener(
      'resize',
      // _.debounce(() => resizeHandler, 1000),
      resizeHandler,
    );

    function resizeHandler() {
      if (containerRef.current && currentFrameData) {
        setCanvasDimensionRatio(
          containerRef.current.clientHeight / containerRef.current.clientWidth,
        );
        rendererRef.current!.setDimensions({
          width:
            canvasDimensionRatio > ratio169
              ? canvasRef.current!.clientWidth
              : canvasRef.current!.clientHeight * 1.7777777778,
          height:
            canvasDimensionRatio > ratio169
              ? canvasRef.current!.clientWidth * ratio169
              : canvasRef.current!.clientHeight,
        });
        rendererRef.current!.render(currentFrameData);
      }
    }

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [
    canvasDimensionRatio,
    canvasHeight,
    currentFrameData,
    setCurrentFrameData,
    trackCallback,
    type,
  ]);

  useEffect(() => {
    if (rendererRef.current && currentFrameData) {
      if (type === 'screen') {
        const newFrameData = Object.assign({}, currentFrameData);

        newFrameData.data.keypoints.forEach((element) => {
          if (element.position.x < 0) {
            element.position.x = 0;
          } else if (element.position.x > 640) {
            element.position.x = 640;
          }

          if (element.position.y < 0) {
            element.position.y = 0;
          } else if (element.position.y > 480) {
            element.position.y = 480;
          }
        });

        rendererRef.current.render(newFrameData);
      } else {
        rendererRef.current.render(currentFrameData);
      }
    }
  }, [type, currentFrameData]);

  return (
    <CanvasContainer
      className={canvasDimensionRatio > ratio169 ? 'r-vertical' : 'r-horizontal'}
      ref={containerRef}
    >
      <div
        className="canvas-wrap"
        style={{ maxWidth: type === 'screen' ? canvasHeight * 1.7777777778 : undefined }}
      >
        <canvas ref={canvasRef} />
      </div>
    </CanvasContainer>
  );
};

const CanvasContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;

  .canvas-wrap {
    height: 100%;
    margin: 0 auto;
  }

  .canvas-container {
    width: 100% !important;
    height: 100% !important;
  }

  &.r-vertical .canvas-container {
    top: 50%;
    width: 100% !important;
    height: auto !important;
    padding-top: 56.25%;
    transform: translateY(-50%);
  }

  .lower-canvas {
    width: 100% !important;
    height: 100% !important;
    background-color: transparent;
  }

  .upper-canvas {
    width: 100% !important;
    height: 100% !important;
  }

  .tooltip-wrap {
    position: absolute;
    top: 0;
    left: 0;

    & > div {
      position: absolute !important;
    }
  }
`;

export default SkeletonCanvas;
